import type { RefObject } from "react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "wouter";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { DEMO_KEYS, type DemoKey } from "./demo-data";

export type InputMode = "url" | "pdf" | "text";

export interface FormViewProps {
  inputMode: InputMode;
  setInputMode: (v: InputMode) => void;
  linkedinUrl: string;
  setLinkedinUrl: (v: string) => void;
  pdfFile: File | null;
  setPdfFile: (v: File | null) => void;
  freeText: string;
  setFreeText: (v: string) => void;
  /** Disciplines available on the selected model. Empty → cross-only model;
   *  the discipline picker is hidden. */
  disciplines: { id: string; label: string }[];
  /** Discipline to score the candidate against (`null` for a cross-only run). */
  disciplineId: string | null;
  setDisciplineId: (v: string | null) => void;
  fileInputRef: RefObject<HTMLInputElement>;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: () => void;
  onLoadDemo: (key: DemoKey) => void;
  submitting: boolean;
  /** When true, the run will return the debug payload (prompts, raw
   *  LLM outputs, V2 features) for the in-app inspector. */
  debugMode: boolean;
  setDebugMode: (v: boolean) => void;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / k ** i).toFixed(1)} ${sizes[i]}`;
}

export function FormView(props: FormViewProps) {
  const [dragOver, setDragOver] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file?.name.toLowerCase().endsWith(".pdf")) {
        props.setPdfFile(file);
      }
    },
    [props.setPdfFile],
  );

  const needsDiscipline = props.disciplines.length > 0;
  const submitDisabled =
    props.submitting ||
    (props.inputMode === "url" && !props.linkedinUrl.trim()) ||
    (props.inputMode === "pdf" && !props.pdfFile) ||
    (props.inputMode === "text" && props.freeText.trim().length < 50) ||
    (needsDiscipline && !props.disciplineId);

  // Keyboard surface — Cmd/Ctrl+Enter submits when the form is valid.
  // Works from any input on the page; lets the user keep their hands on
  // the keyboard during high-volume screening sessions.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Enter") return;
      if (!(e.metaKey || e.ctrlKey)) return;
      if (submitDisabled) return;
      e.preventDefault();
      props.onSubmit();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [submitDisabled, props.onSubmit]);

  return (
    <div className="ci-stripe ci-stripe-page">
      <div className="ci-narrow fade-up" style={{ width: "100%" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 8,
            marginBottom: 12,
          }}
        >
          <Link
            href="/labs/level-assessment/calibration"
            data-testid="labs-la-calibration-link"
            style={{
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: "var(--r-ink-muted)",
              textDecoration: "none",
              padding: "6px 10px",
              borderRadius: 6,
              border: "1px solid var(--r-ink-faint, #e9e3d8)",
            }}
          >
            📊 Calibrar
          </Link>
          <Link
            href="/labs/level-assessment/config"
            data-testid="labs-la-config-link"
            style={{
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: "var(--r-ink-muted)",
              textDecoration: "none",
              padding: "6px 10px",
              borderRadius: 6,
              border: "1px solid var(--r-ink-faint, #e9e3d8)",
            }}
          >
            ⚙ Configurar avaliação
          </Link>
        </div>
        <div className="card">
          <div className="card-head">
            <div className="card-head-left">
              <h2>Perfil do candidato</h2>
              <div className="sub">
                Cole o LinkedIn, anexe PDF, ou descreva a trajetória em texto livre.
              </div>
            </div>
            <div className="card-head-right">
              <div className="seg" role="tablist" aria-label="Fonte do perfil">
                <button
                  type="button"
                  role="tab"
                  aria-selected={props.inputMode === "url"}
                  className={cn(props.inputMode === "url" && "active")}
                  onClick={() => props.setInputMode("url")}
                  data-testid="labs-la-mode-url"
                >
                  URL LinkedIn
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={props.inputMode === "pdf"}
                  className={cn(props.inputMode === "pdf" && "active")}
                  onClick={() => props.setInputMode("pdf")}
                  data-testid="labs-la-mode-pdf"
                >
                  PDF
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={props.inputMode === "text"}
                  className={cn(props.inputMode === "text" && "active")}
                  onClick={() => props.setInputMode("text")}
                  data-testid="labs-la-mode-text"
                >
                  Texto livre
                </button>
              </div>
            </div>
          </div>

          <div className="card-body padded">
            {props.inputMode === "url" && (
              <div className="field">
                <Label htmlFor="linkedin-url" className="field-label">
                  Perfil do LinkedIn
                </Label>
                <Input
                  id="linkedin-url"
                  value={props.linkedinUrl}
                  onChange={(e) => props.setLinkedinUrl(e.target.value)}
                  placeholder="linkedin.com/in/joaocandidato"
                  data-testid="labs-la-linkedin"
                  autoFocus
                />
                <p className="field-helper">
                  Cole o link completo ou apenas o username (ex.: joaocandidato).
                </p>
              </div>
            )}

            {props.inputMode === "pdf" && (
              <div className="field">
                <Label className="field-label" htmlFor="labs-la-pdf-input">
                  PDF do perfil LinkedIn
                </Label>
                <input
                  id="labs-la-pdf-input"
                  ref={props.fileInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  className="hidden"
                  onChange={props.onFileChange}
                  data-testid="labs-la-pdf-input"
                  style={{ display: "none" }}
                />
                {props.pdfFile ? (
                  <div
                    className="file-chip fade-up"
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="name">{props.pdfFile.name}</div>
                      <div
                        className="meta"
                        style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}
                      >
                        <span className="pill forest no-dot">Anexado</span>
                        <span>{formatFileSize(props.pdfFile.size)} · PDF</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn ghost"
                      onClick={() => props.setPdfFile(null)}
                      aria-label="Remover arquivo"
                    >
                      Remover
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => props.fileInputRef.current?.click()}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={cn("dropzone", dragOver && "over")}
                    data-testid="labs-la-pdf-pick"
                  >
                    <div className="dz-title">
                      {dragOver ? "Solte o arquivo aqui" : "Arrastar PDF ou clicar para selecionar"}
                    </div>
                    <div className="dz-sub">Perfil LinkedIn exportado em PDF · máx. 10 MB</div>
                  </button>
                )}
              </div>
            )}

            {props.inputMode === "text" && (
              <div className="field">
                <Label htmlFor="free-text" className="field-label">
                  Texto livre da trajetória
                </Label>
                <Textarea
                  id="free-text"
                  value={props.freeText}
                  onChange={(e) => props.setFreeText(e.target.value)}
                  placeholder="Cole notas de entrevista, bio, currículo cru — qualquer texto que descreva a carreira do candidato. Mínimo 50 caracteres."
                  rows={10}
                  data-testid="labs-la-freetext"
                />
                <p className="field-helper">
                  Use quando não houver LinkedIn ou PDF disponível. Quanto mais detalhe sobre escopo
                  de atuação, projetos liderados e responsabilidades, melhor a inferência.
                </p>
              </div>
            )}
          </div>
        </div>

        {needsDiscipline && (
          <div className="card" style={{ marginTop: 16 }}>
            <div className="card-head">
              <div className="card-head-left">
                <h2>Disciplina</h2>
                <div className="sub">
                  As competências específicas avaliadas dependem da disciplina escolhida. As
                  competências comuns são avaliadas em qualquer caso.
                </div>
              </div>
            </div>
            <div className="card-body padded">
              <div className="field">
                <Label className="field-label" htmlFor="discipline-picker">
                  Disciplina
                </Label>
                <Select
                  value={props.disciplineId ?? ""}
                  onValueChange={(v) => props.setDisciplineId(v || null)}
                >
                  <SelectTrigger
                    id="discipline-picker"
                    style={{ height: 36, minWidth: 260 }}
                    data-testid="labs-la-discipline"
                  >
                    <SelectValue placeholder="Escolher disciplina" />
                  </SelectTrigger>
                  <SelectContent>
                    {props.disciplines.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 24,
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: "var(--r-ink-muted)", fontSize: 13 }}>Ver exemplo:</span>
            <Select onValueChange={(v) => props.onLoadDemo(v as DemoKey)}>
              <SelectTrigger
                style={{ height: 36, minWidth: 220 }}
                data-testid="labs-la-demo-picker"
              >
                <SelectValue placeholder="Escolher exemplo" />
              </SelectTrigger>
              <SelectContent>
                {DEMO_KEYS.map((d) => (
                  <SelectItem key={d.key} value={d.key}>
                    {d.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            {/* Inspection toggle — opt-in modo debug. Makes the backend
                capture every intermediate state + adds a 4ª aba "Debug" to
                the results view. Off by default so normal flows stay lean. */}
            <label
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12,
                color: "var(--r-ink-muted)",
                cursor: "pointer",
              }}
              title="Mostra o detalhamento técnico da avaliação (uso interno)"
            >
              <input
                type="checkbox"
                checked={props.debugMode}
                onChange={(e) => props.setDebugMode(e.target.checked)}
                data-testid="labs-la-debug-toggle"
              />
              Modo inspeção
            </label>
            {!submitDisabled && !props.submitting && (
              <span
                style={{
                  fontSize: 11,
                  color: "var(--r-ink-muted)",
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  fontWeight: 600,
                }}
              >
                ⌘↵ avaliar
              </span>
            )}
            <button
              type="button"
              className="btn primary"
              onClick={props.onSubmit}
              disabled={submitDisabled}
              data-testid="labs-la-submit"
            >
              {props.submitting ? "Avaliando…" : "Avaliar nível"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
